import { enqueueSnackbar } from 'notistack';
import { useMemo, useState, useEffect } from 'react';

import { Box, Stack } from '@mui/system';
import {
  Card,
  Button,
  useTheme,
  TextField,
  CardHeader,
  Typography,
  CardActions,
  CardContent
} from '@mui/material';

import { useAuthContext } from 'src/hooks/auth/use-auth-context';

import { useTranslate } from 'src/locales';
import { AccountRole } from 'src/services/organisation/organisation.enum';
import { OrganizationSetting } from 'src/services/organisation/organisation.types';
import {
  useGetOrganizationSettingsQuery,
  useUpdateOrganizationSettingsMutation
} from 'src/services/organisation/organisation.service';

import Iconify from 'src/components/iconify';

export default function InterviewPhasesSetting() {

  const { t } = useTranslate();
  const theme = useTheme();

  const { organization, account } = useAuthContext();

  const { currentData: settingsData } = useGetOrganizationSettingsQuery(
    organization.id
  );

  const [updateSettings] = useUpdateOrganizationSettingsMutation();

  const [setting, setSetting] = useState<number>(0);

  const maxInterviewPhases = useMemo(() => {

    if (settingsData) {
      const interviewPhasesSetting = settingsData.find(
        (settingVal) => settingVal.setting_type === OrganizationSetting.MAX_INTERVIEW_PHASES
      );

      if (interviewPhasesSetting) {
        return interviewPhasesSetting.setting_value as number;
      }
    }

    return false;

  }, [settingsData]);

  useEffect(() => {
    if (maxInterviewPhases) {
      setSetting(maxInterviewPhases as number);
    }
  }, [setSetting, maxInterviewPhases]);

  const onMaxInterviewPhaseUpdate = async (value: number) => {

    setSetting(value);

    try {
      const settingsToUpdate = [];

      if (!settingsData) return;

      const interviewPhaseSetting = settingsData.find(
        (settingVal) => settingVal.setting_type === OrganizationSetting.MAX_INTERVIEW_PHASES
      );

      if (interviewPhaseSetting) {
        settingsToUpdate.push({
          id: interviewPhaseSetting.id,
          value
        });
      }

      if (settingsToUpdate.length === 0) return;

      await updateSettings({
        organizationId: organization.id,
        settings: settingsToUpdate,
      }).unwrap();

      enqueueSnackbar(t('admin-organization.settings.business_settings.update.success'), { variant: 'success' });
    } catch (e) {
      enqueueSnackbar(t('admin-organization.settings.business_settings.update.default_error'), { variant: 'error' });
    }
  }

  const canEditSettings = useMemo(() => {
    if (!account) return false;
    return [AccountRole.Admin, AccountRole.Op_Manager].includes(account.org_role);
  }, [account]);

  return (
    <Box sx={{ width: '100%' }}>
      <Card
        sx={{
          width: '100%',
          position: 'relative',
          boxShadow: 0
        }}
      >
        <CardHeader
          avatar={
            <Iconify
              icon="fluent:video-person-16-filled"
              sx={{
                width: 30,
                height: 30,
                color: theme.palette.primary.main,
              }}
            />
          }
          title={
            <Typography variant="h6" lineHeight={1.75}>
              {t('admin-organization.settings.max_interview_phases.title')}
            </Typography>
          }
        />

        <CardContent>

          <Stack direction="column" justifyContent="center" sx={{ mt: 2, width: '100%' }} gap={2}>

            <TextField
              label={t('admin-organization.settings.max_interview_phases.label')}
              type="number"
              value={setting}
              onChange={(e) => setSetting(Number(e.target.value))}
              disabled={!canEditSettings}
              inputProps={{ min: 1, max: 10 }}
              helperText={t('admin-organization.settings.max_interview_phases.helper_text')}
              fullWidth
            />

          </Stack>
        </CardContent>
        <CardActions sx={{ justifyContent: 'flex-end', p: 2 }}>
          <Button
            variant="contained"
            onClick={() => onMaxInterviewPhaseUpdate(setting)}
            disabled={!canEditSettings}
          >
            {t('common.update')}
          </Button>
        </CardActions>
      </Card>
    </Box>
  )
}